import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { ENTITLEMENT_BLOCK_EVENT, entitlementBlockCode } from '../lib/entitlement-block.js';
import { isAuthenticated, logout, getIdTokenClaims, startSilentLogin, watchSessionRenewal, takeSessionRenewedNotice } from '../lib/auth.js';
import { loginPath } from '../lib/login-path.js';
import { pendingClaimRedirect } from '../lib/pending-claim.js';
import { customerSectionPath, homeScope, landingRoute, locate, navigationFor, toNavClaims } from '../lib/navigation.js';
import { customerOptions, fetchAllCustomers } from '../lib/customer-options.js';
import { BRAND, ABOUT_LINE } from '../lib/brand.js';
import { buildUserProfile } from '../lib/user-profile.js';
import TopBar from './TopBar.jsx';
import Sidebar from './Sidebar.jsx';
import CustomerSwitcher from './CustomerSwitcher.jsx';
import CustomerLogo from './CustomerLogo.jsx';
import { useCustomerLogo } from '../lib/use-customer-logo.js';
import { useActivityBadge } from '../lib/use-activity-badge.js';
import { readsWatchContent } from '../lib/tenant-access.js';
import { STAFF_ACT_MESSAGE, staffReadOnly } from '../lib/staff-act.js';
import QuickFind from './QuickFind.jsx';
import AppSwitcher from './AppSwitcher.jsx';
import NavDrawer from './NavDrawer.jsx';
import PageState from './PageState.jsx';
import BrandFooter from './BrandFooter.jsx';
import ExternalLink from './ExternalLink.jsx';
import EntitlementBlocked from '../pages/EntitlementBlocked.jsx';

/**
 * The workbench layout (portal workbench spec, 2026-09-29; it replaces the old Layout):
 * a skip link, the TopBar (header), the Sidebar (nav), the page (main) and
 * the BrandFooter (footer). What the navigation shows comes from
 * lib/navigation.js and is UX only: the API enforces every rule itself.
 */
export default function AppShell() {
  const nav = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  // ENTRAID-332: set when the backend answers 403 subscription_ended or trust_role_missing.
  const [blocked, setBlocked] = useState(null);
  // Operator "View as client": React state only, reset on reload on purpose.
  const [impersonating, setImpersonating] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const [fleet, setFleet] = useState({ items: null, loading: false, error: null });
  // react-router 6 rebuilds the useNavigate function whenever the pathname
  // changes; the mount-only effect below reads the latest one from this ref
  // instead of depending on it, so GET /me runs once per session, not per click.
  const navRef = useRef(nav);
  navRef.current = nav;

  useEffect(() => {
    // Signed out or rejected (401): the email-first /login page, never the
    // hosted UI directly, and back to the page first requested afterwards.
    // Mount only (deps []), so the location captured is the entry one.
    const entry = location.pathname + location.search;
    const here = loginPath(entry);
    if (!isAuthenticated()) {
      // Tokens are memory-only (SEC-009), so a reload lands here: first try a
      // silent sign-in against the hosted UI session; only when that was just
      // tried (no session) show the email-first page.
      if (startSilentLogin(entry)) return;
      navRef.current(here);
      return;
    }
    api.me().then(setMe).catch((err) => {
      // A blocked customer sees the full-page message, never the login page again; but a pending onboarding claim of
      // this tab runs first (ENTRAID-350): it adds a usable customer to the login.
      const code = entitlementBlockCode(err);
      const claimFirst = code ? pendingClaimRedirect() : null;
      if (claimFirst) navRef.current(claimFirst);
      else if (code) setBlocked(code);
      else navRef.current(here);
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ENTRAID-342: renew the session before the token expires, and when the tab is
  // shown again, coming back to the current route. Memory-only tokens, no refresh token.
  const returnToRef = useRef('/');
  returnToRef.current = location.pathname + location.search;
  useEffect(() => {
    if (!isAuthenticated()) return undefined;
    return watchSessionRenewal({ getReturnTo: () => returnToRef.current });
  }, []);

  // After a renewal that dropped a write (never replayed), say so once.
  const [renewedNotice, setRenewedNotice] = useState(false);
  useEffect(() => {
    if (takeSessionRenewedNotice()) setRenewedNotice(true);
  }, []);

  // Any later request refused with an entitlement code (api.js dispatches the event) shows the same page.
  useEffect(() => {
    const onBlocked = (e) => {
      const claimFirst = pendingClaimRedirect();
      if (claimFirst) navRef.current(claimFirst);
      else setBlocked(e?.detail?.code ?? 'subscription_ended');
    };
    window.addEventListener(ENTITLEMENT_BLOCK_EVENT, onBlocked);
    return () => window.removeEventListener(ENTITLEMENT_BLOCK_EVENT, onBlocked);
  }, []);

  // ENTRAID-307: a user of several customers gets the roles of the customer the current page is about.
  const pathCustomerId = locate(location.pathname).scope?.customerId ?? null;
  const navClaims = useMemo(() => toNavClaims(me, impersonating, pathCustomerId), [me, impersonating, pathCustomerId]);
  const realIsOperator = me?.user?.isOperator === true;
  // The customer's own logo in the header: customer scope, any member, and only when the admin approved it in Branding (server rule).
  const logoScope = locate(location.pathname).scope ?? homeScope(navClaims);
  const logoCustomerId = logoScope?.kind === 'customer' ? logoScope.customerId : null;
  const customerLogo = useCustomerLogo(logoCustomerId, !blocked);
  // The Activity badge: unanswered critical Watch events, only for a viewer who reads Watch content of this customer.
  const badgeCustomerId = logoCustomerId && !blocked && readsWatchContent(me, navClaims, logoCustomerId) ? logoCustomerId : null;
  const activityBadge = useActivityBadge(badgeCustomerId, location.pathname);

  // Operators only: the switcher lists the existing operator-only GET /customers.
  useEffect(() => {
    if (!realIsOperator) return undefined;
    let cancelled = false;
    setFleet({ items: null, loading: true, error: null });
    fetchAllCustomers(api.listCustomers)
      .then((items) => {
        if (!cancelled) setFleet({ items, loading: false, error: null });
      })
      .catch((e) => {
        if (!cancelled) setFleet({ items: null, loading: false, error: e.message });
      });
    return () => {
      cancelled = true;
    };
  }, [realIsOperator]);

  // Any navigation closes the phone drawer.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const startImpersonating = useCallback((customerId, companyName) => {
    setImpersonating({ customerId, companyName });
    nav(customerSectionPath(customerId, 'overview'));
  }, [nav]);

  const stopImpersonating = useCallback(() => {
    const returnTo = impersonating?.customerId;
    setImpersonating(null);
    nav(returnTo ? customerSectionPath(returnTo, 'overview') : '/');
  }, [nav, impersonating]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  if (loading) return <Splash />;
  if (blocked) return <EntitlementBlocked code={blocked} onSignOut={logout} />;

  const located = locate(location.pathname);
  const scope = located.scope ?? homeScope(navClaims);
  const { groups } = navigationFor(navClaims, scope);
  const badges = activityBadge && activityBadge.count > 0
    ? { activity: { text: `${activityBadge.count}${activityBadge.more ? '+' : ''}`, label: `${activityBadge.count}${activityBadge.more ? ' or more' : ''} unanswered critical` } }
    : {};
  const options = customerOptions({ me, navClaims, impersonating, operatorCustomers: fleet.items });
  const showSwitcher = Boolean(scope) && (navClaims.isOperator || options.length > 0);
  // "View as client" lives in the switcher menu (ENTRAID-347 redesign): operators, a customer scope, not already in it.
  const viewAs = realIsOperator && !impersonating && scope?.kind === 'customer'
    ? () => startImpersonating(scope.customerId, options.find((o) => o.customerId === scope.customerId)?.name ?? scope.customerId)
    : null;
  const switcherProps = { navClaims, scope, pathname: location.pathname, options, loading: fleet.loading, error: fleet.error, onViewAsClient: viewAs };
  // ENTRAID-307 (Decision 1): staff on a customer below the top tier only see; the portal refuses their changes.
  const scopedCustomer = scope?.kind === 'customer'
    ? (fleet.items ?? []).find((c) => c.customerId === scope.customerId) ?? (me?.customer?.customerId === scope.customerId ? me.customer : null)
    : null;
  const staffViewOnly = staffReadOnly(me, scopedCustomer);
  const profile = buildUserProfile({ claims: getIdTokenClaims(), me, tenantName: impersonating?.companyName ?? null });
  function skipToMain(event) {
    event.preventDefault();
    document.getElementById('main')?.focus();
  }

  return (
    <div className="flex min-h-screen flex-col bg-ipc-surface">
      <a
        href="#main"
        onClick={skipToMain}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-ipc-primary focus:shadow"
      >
        Skip to main content
      </a>
      <TopBar
        productTo={landingRoute(navClaims) ?? '/'}
        menuOpen={menuOpen}
        menuButtonRef={menuButtonRef}
        onToggleMenu={() => setMenuOpen((o) => !o)}
        switcher={showSwitcher ? <CustomerSwitcher {...switcherProps} /> : null}
        customerLogo={customerLogo ? <CustomerLogo logo={customerLogo} /> : null}
        quickFind={<QuickFind navClaims={navClaims} scope={scope} pathname={location.pathname} options={options} groups={groups} />}
        isOperator={navClaims.isOperator}
        profile={profile}
        onSignOut={logout}
      />
      {impersonating && <ImpersonationBanner impersonating={impersonating} onExit={stopImpersonating} />}
      {staffViewOnly && <StaffReadOnlyBanner />}
      <div className="flex min-w-0 flex-1">
        {groups.length > 0 && (
          <aside className="hidden shrink-0 border-r border-ipc-border bg-white md:block md:w-16 lg:w-60">
            <Sidebar groups={groups} activeId={located.itemId} variant="rail" badges={badges} />
          </aside>
        )}
        <main id="main" tabIndex={-1} className="min-w-0 flex-1 px-4 py-6 focus:outline-none sm:px-6 lg:px-10 lg:py-8">
          <div className="mx-auto max-w-7xl">
            {renewedNotice && (
              <div role="status" data-testid="session-renewed" className="mb-3 flex items-center justify-between gap-2 rounded border border-ipc-border bg-white px-3 py-2 text-sm text-slate-700">
                <span>Your session was renewed, please try again.</span>
                <button type="button" onClick={() => setRenewedNotice(false)} className="rounded px-2 py-1 text-ipc-light underline">Dismiss</button>
              </div>
            )}
            <Outlet context={{ me, impersonating, startImpersonating, stopImpersonating, navClaims }} />
          </div>
        </main>
      </div>
      <BrandFooter />
      <NavDrawer open={menuOpen} onClose={closeMenu} returnFocusRef={menuButtonRef}>
        {showSwitcher && (
          <section aria-label="Customer">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Customer</p>
            <CustomerSwitcher {...switcherProps} inline onNavigate={closeMenu} />
          </section>
        )}
        {groups.length > 0 && <Sidebar groups={groups} activeId={located.itemId} variant="drawer" onNavigate={closeMenu} badges={badges} />}
        <section aria-label="IPC Global apps">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-600">IPC Global apps</p>
          <AppSwitcher isOperator={navClaims.isOperator} inline />
        </section>
        <ExternalLink href={BRAND.supportUrl} className="text-sm text-ipc-light underline">Help</ExternalLink>
        <p className="text-xs text-slate-600">{ABOUT_LINE}</p>
      </NavDrawer>
    </div>
  );
}

/**
 * The slim operator bar under the top bar while an operator views the portal as the client (ENTRAID-347 redesign):
 * a white bar with a 4 px warn rule and a warn dot, the customer named in ink text (amber is never a text color),
 * and Exit. It replaces the full amber banner above the top bar.
 */
function ImpersonationBanner({ impersonating, onExit }) {
  return (
    <div className="border-b border-ipc-border bg-white" style={{ borderLeft: '4px solid var(--warn)' }} data-testid="viewing-as-client">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-1.5 sm:px-6 lg:px-10">
        <p className="flex min-w-0 items-center gap-2 text-sm text-ipc-ink" role="status">
          <span aria-hidden="true" className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: 'var(--warn-dot)' }} />
          <span className="min-w-0 break-words">
            Viewing as client: <strong>{impersonating.companyName || impersonating.customerId}</strong>
          </span>
          <span className="min-w-0 break-all font-mono text-xs text-ipc-muted">({impersonating.customerId})</span>
        </p>
        <button type="button" onClick={onExit} className="btn-secondary px-3 py-1 text-sm">
          Exit client view
        </button>
      </div>
    </div>
  );
}

/**
 * IPC Global staff on a customer below the top tier (ENTRAID-307): a read-only view. One line, the same words the
 * portal answers with when a change is refused.
 */
function StaffReadOnlyBanner() {
  return (
    <div className="border-b border-ipc-border bg-white" style={{ borderLeft: '4px solid var(--warn)' }} data-testid="staff-read-only">
      <p className="px-4 py-1.5 text-sm text-ipc-ink sm:px-6 lg:px-10" role="status">
        <strong>Read only.</strong> {STAFF_ACT_MESSAGE}
      </p>
    </div>
  );
}

/** Before the session is known: the page skeleton (PageState loading), never a bare "Loading...". */
function Splash() {
  return (
    <div className="mx-auto min-h-screen max-w-7xl px-4 py-6 sm:px-6 lg:px-10 lg:py-8">
      <PageState state="loading" label="Loading onGuard" />
    </div>
  );
}
